import type {
  MouseEventHandler,
  TargetedMouseEvent,
  TargetedTouchEvent,
  TouchEventHandler,
  VNode,
} from 'preact';
import { useRef } from 'preact/hooks';

import type { FacetSliderMode } from 'Models/uiConfig/facetsConfig';
import { getNumPercent } from 'Utils/ranged';

const replaceByIndex = (array: number[], index: number, value: number): number[] =>
  array.map((v, i) => (i === index ? value : v));

export interface SliderStripProps {
  field: string;
  selected: number[];
  mode: FacetSliderMode;
  color?: string;
  onChangeStarted?: () => void;
  onChange: (selected: number[]) => void;
  onChangeFinished: () => void;
}

type SliderStartEvent = TargetedMouseEvent<HTMLDivElement> | TargetedTouchEvent<HTMLDivElement>;
type SliderDocumentMoveEvent = MouseEvent | TouchEvent;
type SliderDragEvent = SliderStartEvent | SliderDocumentMoveEvent;

export default function SliderStrip({
  field,
  selected,
  mode,
  color,
  onChangeStarted = () => {},
  onChange,
  onChangeFinished,
}: SliderStripProps): VNode<object> {
  const activeMarkIndex = useRef(-1);

  const containerProps: {
    className: string;
    style?: { left: string; right: string };
    onMouseDown?: MouseEventHandler<HTMLDivElement>;
    onTouchStart?: TouchEventHandler<HTMLDivElement>;
  } = { className: 'cm_strip-container' };
  const selectedProps: {
    className: string;
    style: { backgroundColor?: string; left?: string; right?: string };
  } = {
    className: 'cm_strip selected',
    style: color ? { backgroundColor: color } : {},
  };
  if (mode === 'Lock') {
    return (
      <div {...containerProps}>
        <div {...selectedProps} />
      </div>
    );
  }

  function fit(value: number) {
    const active = activeMarkIndex.current;
    const i = active === 0 ? 1 : active === 1 ? 0 : -1;
    const [min, max] = replaceByIndex([0, 100], i, selected[i]);
    return value < min ? min : value > max ? max : value;
  }
  function handleMove(x: number, bRect: DOMRect) {
    const coord = fit(getNumPercent(x, [bRect.left, bRect.right]));

    const dist = (i: number) => Math.abs(coord - selected[i]);
    const d0 = dist(0);
    const d1 = dist(1);
    if (activeMarkIndex.current < 0 && (d0 || d1)) {
      const catchMinMark = mode === 'LowerBound' || (d0 === d1 && coord < selected[0]);
      const catchMaxMark = mode === 'UpperBound' || (d0 === d1 && coord > selected[0]);

      activeMarkIndex.current = catchMinMark ? 0 : catchMaxMark ? 1 : d0 < d1 ? 0 : 1;
    }
    const index = activeMarkIndex.current;

    onChange(dist(index) ? replaceByIndex(selected, index, coord) : selected);
  }

  function getDragClientX(event: SliderDragEvent): number | null {
    if ('changedTouches' in event) {
      return event.changedTouches[0]?.clientX ?? null;
    }
    return 'clientX' in event ? event.clientX : null;
  }

  function handleDragEvent(event: SliderDragEvent, bRect: DOMRect) {
    const clientX = getDragClientX(event);
    if (clientX !== null) {
      handleMove(clientX, bRect);
    }
  }

  const createHandler = (isMouse: boolean) => (event: SliderStartEvent) => {
    event.preventDefault();
    if (!(event.currentTarget instanceof HTMLElement)) {
      return;
    }

    onChangeStarted();
    const bRect = event.currentTarget.getBoundingClientRect();

    if (isMouse) {
      const moveHandler = (moveEvent: MouseEvent) => handleDragEvent(moveEvent, bRect);
      const endHandler = () => {
        onChangeFinished();
        activeMarkIndex.current = -1;
        document.removeEventListener('mousemove', moveHandler);
        document.removeEventListener('mouseup', endHandler);
      };

      handleDragEvent(event, bRect);
      document.addEventListener('mousemove', moveHandler);
      document.addEventListener('mouseup', endHandler);
      return;
    }

    const moveHandler = (moveEvent: TouchEvent) => handleDragEvent(moveEvent, bRect);
    const endHandler = () => {
      onChangeFinished();
      activeMarkIndex.current = -1;
      document.removeEventListener('touchmove', moveHandler);
      document.removeEventListener('touchend', endHandler);
    };

    handleDragEvent(event, bRect);
    document.addEventListener('touchmove', moveHandler);
    document.addEventListener('touchend', endHandler);
  };
  containerProps.onMouseDown = createHandler(true);
  containerProps.onTouchStart = createHandler(false);

  const marks = selected.map((s, i) => {
    if ((mode === 'UpperBound' && i === 0) || (mode === 'LowerBound' && i === 1)) {
      return null;
    }
    const markContainerProps = {
      className: 'cm_mark-container',
      style: { left: `${s}%` },
    };
    const markProps: { className: string; style?: { borderColor: string } } = { className: 'cm_mark' };
    if (color) {
      markProps.style = { borderColor: color };
    }
    const key = `${field}_SlMrk${['Lt', 'Rt'][i]}`;
    return (
      <div {...markContainerProps} key={key}>
        <div {...markProps} />
      </div>
    );
  });
  selectedProps.style.left = `${selected[0]}%`;
  selectedProps.style.right = `${100 - selected[1]}%`;
  return (
    <div {...containerProps}>
      <div className="cm_strip" />
      <div {...selectedProps} />
      {marks}
    </div>
  );
}
